import { AvailabilitySlot } from '@theraply/api-client';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 14 }, (_, i) => 8 + i); // 8 AM to 10 PM

interface WeeklyScheduleProps {
  slots: AvailabilitySlot[];
  onSlotClick: (slot: AvailabilitySlot) => void;
  onEmptySlotClick: (dayOfWeek: number, startTime: string) => void;
}

export default function WeeklySchedule({ slots, onSlotClick, onEmptySlotClick }: WeeklyScheduleProps) {
  const getSlotColor = (sessionType: string) => {
    switch (sessionType) {
      case 'video':
        return 'bg-blue-100 border-blue-300 text-blue-900';
      case 'in_person':
        return 'bg-green-100 border-green-300 text-green-900';
      case 'both':
        return 'bg-purple-100 border-purple-300 text-purple-900';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-900';
    }
  };

  const getSlotLabel = (sessionType: string) => {
    switch (sessionType) {
      case 'video':
        return '📹 Video';
      case 'in_person':
        return '🏢 In-Person';
      case 'both':
        return '🔄 Both';
      default:
        return '';
    }
  };

  const isSlotActive = (dayOfWeek: number, hour: number) => {
    return slots.some((slot) => {
      if (slot.dayOfWeek !== dayOfWeek) return false;
      const [startHour] = slot.startTime.split(':').map(Number);
      const [endHour] = slot.endTime.split(':').map(Number);
      return hour >= startHour && hour < endHour;
    });
  };

  const getSlotForTimeBlock = (dayOfWeek: number, hour: number) => {
    return slots.find((slot) => {
      if (slot.dayOfWeek !== dayOfWeek) return false;
      const [startHour] = slot.startTime.split(':').map(Number);
      const [endHour] = slot.endTime.split(':').map(Number);
      return hour >= startHour && hour < endHour;
    });
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-8 gap-0 border-b border-gray-200 bg-gray-50">
        <div className="px-4 py-3 text-sm font-semibold text-gray-700">Time</div>
        {DAYS_OF_WEEK.map((day, index) => (
          <div key={index} className="px-4 py-3 text-sm font-semibold text-gray-700 text-center">
            {day}
          </div>
        ))}
      </div>

      {/* Time slots grid */}
      <div className="divide-y divide-gray-200">
        {HOURS.map((hour) => (
          <div key={hour} className="grid grid-cols-8 gap-0 divide-x divide-gray-200">
            {/* Time label */}
            <div className="px-4 py-3 text-xs font-medium text-gray-500 bg-gray-50">
              {String(hour).padStart(2, '0')}:00
            </div>

            {/* Day columns */}
            {DAYS_OF_WEEK.map((_, dayOfWeek) => {
              const slot = getSlotForTimeBlock(dayOfWeek, hour);
              const isActive = isSlotActive(dayOfWeek, hour);

              // Only render the slot once (at the start hour)
              if (slot) {
                const [startHour] = slot.startTime.split(':').map(Number);
                if (hour !== startHour) {
                  return (
                    <div
                      key={`${dayOfWeek}-${hour}`}
                      className="min-h-16 px-2 py-1"
                    />
                  );
                }

                // Calculate height based on duration
                const [, startMin] = slot.startTime.split(':').map(Number);
                const [endHour, endMin] = slot.endTime.split(':').map(Number);
                const duration = (endHour - startHour) * 60 + (endMin - startMin);
                const height = (duration / 60) * 64 + 'px';

                return (
                  <div
                    key={`${dayOfWeek}-${hour}`}
                    className="relative"
                    style={{ minHeight: height }}
                  >
                    <button
                      onClick={() => onSlotClick(slot)}
                      className={`w-full h-full p-2 rounded-md border-2 font-semibold text-xs cursor-pointer hover:shadow-lg transition-shadow ${getSlotColor(slot.sessionType)}`}
                    >
                      <div className="truncate">{getSlotLabel(slot.sessionType)}</div>
                      <div className="text-xs mt-1">
                        {slot.startTime} - {slot.endTime}
                      </div>
                    </button>
                  </div>
                );
              }

              // Empty slot - clickable to create new
              return (
                <button
                  key={`${dayOfWeek}-${hour}`}
                  onClick={() => onEmptySlotClick(dayOfWeek, `${String(hour).padStart(2, '0')}:00`)}
                  className="min-h-16 px-2 py-1 hover:bg-primary-50 transition-colors cursor-pointer border border-transparent hover:border-primary-300"
                >
                  <span className="text-gray-300 text-xs group-hover:text-primary-500">+</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
